import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.addEventListener('click', this.#handleGridMediaClick, { capture: true, signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * In grid/collage presentation, promote clicked media to the first slot.
   * @param {MouseEvent} event
   */
  #handleGridMediaClick = (event) => {
    if (this.presentation !== 'grid') return;

    const grid = this.querySelector('[data-testid="media-gallery-grid"]');
    if (!grid) return;

    const mediaItem = event.target instanceof Element
      ? event.target.closest('.product-media-container')
      : null;
    if (!mediaItem || !grid.contains(mediaItem)) return;
    if (grid.firstElementChild === mediaItem) return;

    // Clicking a gallery image should promote it to main image instead of opening zoom.
    event.preventDefault();
    event.stopPropagation();

    const supportsViewTransition = typeof document.startViewTransition === 'function';
    if (supportsViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.startViewTransition(() => {
        grid.prepend(mediaItem);
        this.#syncMainMediaMarker(grid);
      });
    } else {
      grid.prepend(mediaItem);
      this.#syncMainMediaMarker(grid);
    }

    this.#animatePromotedMedia(mediaItem);
  };

  /**
   * Keep the product-image-transition marker on the first media only.
   * @param {Element} grid
   */
  #syncMainMediaMarker(grid) {
    const items = Array.from(grid.children);
    const hasMainMarker = items.some((item) => item.hasAttribute('data-view-transition-type'));

    if (!hasMainMarker) return;

    items.forEach((item, index) => {
      if (index === 0) {
        item.setAttribute('data-view-transition-type', 'product-image-transition');
      } else {
        item.removeAttribute('data-view-transition-type');
      }
    });
  }

  /**
   * Apply a subtle highlight motion after promoting a media tile.
   * @param {Element} mediaItem
   */
  #animatePromotedMedia(mediaItem) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!(mediaItem instanceof HTMLElement)) return;

    mediaItem.animate(
      [
        { transform: 'scale(0.98)', opacity: 0.75 },
        { transform: 'scale(1)', opacity: 1 },
      ],
      {
        duration: 280,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      }
    );
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
